import { AssociateTaskPerformance, AuditTask } from "../../types";

interface DbUser {
  id: number;
  username: string;
}

const RECENT_CLOSED_LIMIT = 3;

function taskPercent(task: AuditTask): number {
  if (task.target_count <= 0) return 0;
  return Math.min(100, Math.round((task.completed_count / task.target_count) * 100));
}

function isTaskSuccessful(task: AuditTask): boolean {
  return task.completed_count >= task.target_count && task.target_count > 0;
}

export function buildAssociateTaskPerformance(
  tasks: AuditTask[],
  users: DbUser[]
): AssociateTaskPerformance[] {
  const byUserId = new Map<number, AssociateTaskPerformance>();

  for (const user of users) {
    byUserId.set(user.id, {
      userId: user.id,
      username: user.username,
      tasksCompleted: 0,
      tasksCancelled: 0,
      tasksActive: 0,
      leadsDone: 0,
      leadsTarget: 0,
      successRate: 0,
      avgPercent: 0,
      overdueActive: 0,
      recentClosed: [],
    });
  }

  const closedByUser = new Map<number, AuditTask[]>();

  for (const task of tasks) {
    const uid = task.assignee_user_id;
    if (!byUserId.has(uid)) {
      byUserId.set(uid, {
        userId: uid,
        username: task.assignee_username,
        tasksCompleted: 0,
        tasksCancelled: 0,
        tasksActive: 0,
        leadsDone: 0,
        leadsTarget: 0,
        successRate: 0,
        avgPercent: 0,
        overdueActive: 0,
        recentClosed: [],
      });
    }

    const row = byUserId.get(uid)!;

    if (task.status === "completed") {
      row.tasksCompleted += 1;
    } else if (task.status === "cancelled") {
      row.tasksCancelled += 1;
    } else {
      row.tasksActive += 1;
      if (task.overdue) row.overdueActive += 1;
    }

    if (task.status !== "cancelled") {
      row.leadsDone += Math.min(task.completed_count, task.target_count);
      row.leadsTarget += task.target_count;
    }

    if (task.status !== "active") {
      const list = closedByUser.get(uid) ?? [];
      list.push(task);
      closedByUser.set(uid, list);
    }
  }

  const results: AssociateTaskPerformance[] = [];

  for (const row of byUserId.values()) {
    const userTasks = tasks.filter((t) => t.assignee_user_id === row.userId);
    const nonCancelled = userTasks.filter((t) => t.status !== "cancelled");
    const closed = userTasks.filter((t) => t.status !== "active");

    if (nonCancelled.length > 0) {
      const sumPercent = nonCancelled.reduce((s, t) => s + taskPercent(t), 0);
      row.avgPercent = Math.round(sumPercent / nonCancelled.length);
    }

    if (closed.length > 0) {
      const successful = closed.filter(isTaskSuccessful).length;
      row.successRate = Math.round((successful / closed.length) * 100);
    }

    const recent = closedByUser.get(row.userId) ?? [];
    recent.sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime());
    row.recentClosed = recent.slice(0, RECENT_CLOSED_LIMIT);

    const hasAnyTask = userTasks.length > 0;
    if (hasAnyTask) results.push(row);
  }

  results.sort((a, b) => {
    const rateA = a.leadsTarget > 0 ? a.leadsDone / a.leadsTarget : 0;
    const rateB = b.leadsTarget > 0 ? b.leadsDone / b.leadsTarget : 0;
    if (rateB !== rateA) return rateB - rateA;
    return b.tasksCompleted - a.tasksCompleted;
  });

  return results;
}

export function hasClosedTasks(tasks: AuditTask[]): boolean {
  return tasks.some((t) => t.status !== "active");
}

/** Metriche aggregate per l'utente corrente (pannello non-admin). */
export function buildUserTaskPerformance(tasks: AuditTask[]): AssociateTaskPerformance | null {
  if (tasks.length === 0) return null;
  const { assignee_user_id, assignee_username } = tasks[0];
  const rows = buildAssociateTaskPerformance(tasks, [
    { id: assignee_user_id, username: assignee_username },
  ]);
  return rows[0] ?? null;
}
